/**
 * The workspace, built to the design handoff.
 *
 * All ten screens exist. Overview, Requirements and Financial check run on the
 * real determination; the other seven render the design filled with example
 * content under a visible banner, so the shape is reviewable now and each can
 * be wired to real data one at a time (ADR-0038, screens.ts).
 *
 * Every node is built with createElement and textContent — no innerHTML, so
 * nothing from the API can become markup (§3.8).
 */
import { CHECK_LIMITS, financialWorking } from "@verity/engine";
import type { Determination } from "@verity/engine";
import type { Ruleset } from "@verity/rules";
import rulesetJson from "../../../packages/rules/data/appendix-fm.2026-04-11.json";
import { buildWorkspace, type RequirementView, type Workspace } from "./model.js";
import { establishHousehold, loginUrlForHere, LOGOUT_URL, whoami } from "./session.js";
import { renderRail } from "./rail.js";
import { VIEWS, viewFromHash, type View } from "./routing.js";
import { pathwayFor } from "@verity/pathways";
import { renderOnboarding, type OnboardingState } from "./onboarding.js";
import type { AccountPayment, AccountPerson } from "./screens.js";
import {
  renderAccount,
  renderAfter,
  renderAsk,
  renderDocuments,
  renderRisk,
  renderSubmission,
  renderTogether,
} from "./screens.js";

const ruleset = rulesetJson as unknown as Ruleset;

const state: {
  workspace: Workspace | null;
  view: View;
  filter: "all" | "open";
  selected: string | null;
  email: string;
  initials: string;
  /** Everyone on the case, for `People and access`. Empty until loaded. */
  people: AccountPerson[];
  /** What was chosen, for `Plan and payment`. From the server, never a constant here. */
  tier: { name: string; summary: string; display: string } | null;
  /** Where the payment got to. Null when there has never been one. */
  payment: AccountPayment | null;
  /** Whether payment is switched on at all, so the screen offers only what works. */
  paymentConfigured: boolean;
  /** Every version of the case, oldest first. One entry means never revised. */
  history: CaseVersion[];
} = {
  workspace: null,
  view: "overview",
  filter: "all",
  selected: null,
  email: "",
  initials: "",
  people: [],
  tier: null,
  payment: null,
  paymentConfigured: false,
  history: [],
};

/**
 * Begin (or resume) checkout. Returns the Stripe URL, or null when payment is
 * not switched on — the API answers 404 rather than announcing our
 * configuration to the internet (F4).
 */
async function startCheckout(): Promise<string | null> {
  try {
    const response = await fetch("/api/payments", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    if (!response.ok) return null;
    return ((await response.json()) as { url?: string }).url ?? null;
  } catch {
    return null;
  }
}

/** Every version of the case, with what changed (F5). */
export interface CaseVersion {
  version: number;
  verdict: string;
  ruleset_version: string;
  created_at: string;
  changed: string[];
  verdict_moved: boolean;
}

async function loadHistory(): Promise<CaseVersion[]> {
  try {
    const response = await fetch("/api/cases/history", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return [];
    return ((await response.json()) as { versions: CaseVersion[] }).versions;
  } catch {
    return [];
  }
}

/** Where the payment got to, for `Plan and payment`. */
async function loadPayment(): Promise<{
  payment: AccountPayment | null;
  configured: boolean;
}> {
  try {
    const response = await fetch("/api/payments/current", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return { payment: null, configured: false };
    const body = (await response.json()) as { payment: AccountPayment | null; configured: boolean };
    return { payment: body.payment, configured: body.configured };
  } catch {
    return { payment: null, configured: false };
  }
}

/**
 * Wait out the gap between Stripe finishing and the webhook arriving.
 *
 * BOUNDED, and the bound is short. Two extra asks, roughly a second apart, is
 * long enough for a webhook that is going to arrive and short enough that
 * nobody watches a spinner over it. If it has not landed by then the screen
 * shows `pending` honestly and the person can press the button again — the
 * checkout session is reused, so pressing it is free.
 */
async function settlePayment(
  wait: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
): Promise<AccountPayment | null> {
  for (let attempt = 0; attempt < 2; attempt++) {
    await wait(1000);
    const { payment } = await loadPayment();
    if (payment && payment.status !== "pending") return payment;
  }
  return null;
}

/**
 * Who is on the case. Returns an empty list rather than throwing: Account has
 * plenty else to show, and a failed fetch should not blank the screen.
 */
async function loadPeople(): Promise<AccountPerson[]> {
  try {
    const response = await fetch("/api/people", { headers: { accept: "application/json" } });
    if (!response.ok) return [];
    return ((await response.json()) as { people: AccountPerson[] }).people;
  } catch {
    return [];
  }
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

const byId = (id: string) => document.getElementById(id);

function formatDate(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric" }).format(
    new Date(Date.UTC(y, m - 1, d)),
  );
}

function initialsOf(email: string): string {
  const name = email.split("@")[0] ?? "";
  const parts = name.split(/[.\-_]/).filter(Boolean);
  const letters = parts.length >= 2 ? `${parts[0]![0]}${parts[1]![0]}` : name.slice(0, 2);
  return letters.toUpperCase() || "··";
}

/** The handoff's `→`, kept as a character in prose contexts as instructed. */
const ARROW = "→";

/**
 * Field names in ordinary words, for "what changed".
 *
 * OUR OWN FIELD NAMES, not rules-derived copy — these say which box on the
 * check somebody edited, and make no statement about what the Rules ask
 * (CLAUDE.md rule 10). Anything missing falls back to the raw name, which is
 * ugly and honest; inventing a phrase for a field nobody labelled would be
 * neither.
 */
const ANSWER_LABELS: Record<string, string> = {
  sponsor_status: "your partner's status",
  sponsor_receives_benefit: "whether your partner receives a listed benefit",
  stage: "which application you are making",
  intended_date: "when you intend to apply",
  relationship_type: "your relationship",
  date_of_marriage: "the date of your marriage",
  marriage_legally_valid: "whether the marriage is legally valid",
  cohabiting_since: "when you began living together",
  met_in_person: "whether you have met in person",
  route: "how you meet the financial requirement",
  income_from: "whose income counts",
  months_with_employer: "months with your employer",
  gross_annual: "gross annual income",
  savings_amount: "savings",
  savings_held_since: "when the savings were first held",
  english_exemption: "the English language exemption",
  english_level: "your English level",
  legal_right_to_occupy: "the right to occupy your home",
  without_public_funds: "support without public funds",
  life_in_uk_passed: "the Life in the UK test",
  dependent_children: "dependent children",
};

const describeChanged = (fields: string[]): string =>
  fields.map((f) => ANSWER_LABELS[f] ?? f).join(", ");

/**
 * What you told us, and when you changed it (F5).
 *
 * §9.1 in one card: *"if a user says your calculator told me I qualified, you
 * have no record of what you told them."* The versions have been append-only
 * since F2 and nothing has ever shown them.
 *
 * IT STATES WHAT MOVED AND NEVER WHY. "Your verdict changed" is a fact about
 * our record. "Because your income now clears the threshold" is a sentence
 * about the Rules, and those come from the ruleset verbatim or not at all.
 */
function answersCard(history: CaseVersion[]): HTMLElement {
  const card = el("section", "card");
  const head = el("div", "section-head");
  head.append(el("h2", undefined, "Your answers"));
  head.append(el("span", "sub", "Everything on this page is worked out from these."));
  card.append(head);

  const current = history[history.length - 1];
  card.append(
    el(
      "p",
      "interp",
      current
        ? `Version ${current.version}, saved ${formatDate(current.created_at.slice(0, 10))}.`
        : "We do not have your answers yet.",
    ),
  );

  const change = el("a", "btn btn-primary", "Change an answer");
  // Back to the check itself rather than an editor here: the questions are
  // written and reviewed there, half of them are conditional on another, and a
  // second copy would drift (see revisit.ts).
  change.href = "/check/";
  card.append(change);

  // One version means nothing has been revised, and a history of one entry is
  // not a history — it is a date, which the line above already gave.
  if (history.length > 1) {
    card.append(el("p", "kicker", "What has changed"));
    const list = el("div", "next");
    for (const version of [...history].reverse()) {
      if (version.changed.length === 0) continue;
      const row = el("div", "next-row");
      row.append(el("span", "n", String(version.version)));
      const body = el("span", "body");
      body.append(el("span", "t", `You changed ${describeChanged(version.changed)}`));
      body.append(
        el(
          "span",
          "note",
          version.verdict_moved
            ? `${formatDate(version.created_at.slice(0, 10))} · what the check found changed with it`
            : formatDate(version.created_at.slice(0, 10)),
        ),
      );
      row.append(body);
      list.append(row);
    }
    card.append(list);
  }
  return card;
}

/**
 * What is stopping the case moving, at the top of the case.
 *
 * Somebody who skipped payment can see everything — that was the point of
 * letting them skip. But an application that is not progressing, for a reason
 * they could fix in a minute, must not be something they have to go looking in
 * Billing to discover.
 *
 * BOTH HALVES, EVERY TIME. "We cannot assign an adviser" and "nothing is taken
 * until one is" are equally true and pull in opposite directions; giving only
 * the first reads as a demand, only the second as an invitation to ignore it.
 *
 * Amber, never red. Nothing has gone wrong (CLAUDE.md rule 9).
 */
function paymentBlocker(): HTMLElement | null {
  if (!state.paymentConfigured) return null;
  const status = state.payment?.status;
  if (status === "saved" || status === "captured" || status === "action_required") return null;

  const card = el("section", "card blocker");
  card.append(el("p", "kicker", "Before an adviser can be assigned"));
  card.append(
    el(
      "p",
      "blocker-lede",
      state.payment?.status === "pending"
        ? "You started adding your card and did not finish. We cannot assign an adviser or begin work until it is on file — and nothing is taken from it until an adviser has been assigned."
        : "We need your card on file. We cannot assign an adviser or begin work until it is there — and nothing is taken from it until an adviser has been assigned.",
    ),
  );

  const add = el("button", "btn btn-primary", "Add your card");
  add.type = "button";
  add.addEventListener("click", () => {
    add.disabled = true;
    void (async () => {
      const url = await startCheckout();
      if (url) window.location.assign(url);
      else add.disabled = false;
    })();
  });
  card.append(add);
  return card;
}

// ------------------------------------------------------------------ overview

function renderOverview(w: Workspace): HTMLElement {
  const frag = el("div");

  const blocker = paymentBlocker();
  if (blocker) frag.append(blocker);

  const card = el("section", "card");
  const readiness = el("div", "readiness");

  const figure = el("div", "figure");
  figure.append(el("div", "label", "Requirements met"));
  figure.append(el("div", "pct", `${w.readiness}%`));
  readiness.append(figure);
  readiness.append(el("p", "interp", w.readinessNote));
  card.append(readiness);

  const bar = el("div", "bar");
  const fill = el("span");
  fill.style.width = `${w.readiness}%`;
  bar.append(fill);
  card.append(bar);

  const stats = el("div", "stats");
  for (const [n, k] of [
    [String(w.settled), "Settled"],
    [String(w.outstanding), "Still open"],
    [String(w.judged), "A caseworker decides"],
    [String(w.requirements.filter((r) => r.state !== "not-applicable").length), "Requirements in all"],
  ] as [string, string][]) {
    const cell = el("div");
    cell.append(el("div", "n", n));
    cell.append(el("div", "k", k));
    stats.append(cell);
  }
  card.append(stats);
  frag.append(card);
  frag.append(answersCard(state.history));

  if (w.next.length > 0) {
    const head = el("div", "section-head");
    head.append(el("h2", undefined, "What to do next"));
    head.append(el("span", "sub", "Ordered by what holds up the rest."));
    frag.append(head);

    const list = el("div", "next");
    w.next.forEach((item, i) => {
      const row = el("button", "next-row");
      row.type = "button";
      row.append(el("span", "n", String(i + 1)));
      const body = el("span", "body");
      body.append(el("span", "t", item.title));
      body.append(el("span", "note", item.note));
      row.append(body);
      row.append(el("span", "go", ARROW));
      row.addEventListener("click", () => {
        state.selected = item.ruleId;
        go("requirements");
      });
      list.append(row);
    });
    frag.append(list);
  }

  const head2 = el("div", "section-head");
  head2.append(el("h2", undefined, "Where you stand"));
  frag.append(head2);

  const grid = el("div", "categories");
  for (const category of w.categories) {
    const cell = el("button", "category");
    cell.type = "button";
    const top = el("div", "top");
    top.append(el("span", "name", category.name));
    top.append(el("span", "ratio", `${category.settled} / ${category.total}`));
    cell.append(top);

    const cbar = el(
      "div",
      `bar bar--sm${category.fraction === 1 ? "" : category.fraction === 0 ? " bar--none" : " bar--progress"}`,
    );
    const cfill = el("span");
    cfill.style.width = `${Math.round(category.fraction * 100)}%`;
    cbar.append(cfill);
    cell.append(cbar);

    cell.append(el("span", "assessment", category.assessment));
    cell.addEventListener("click", () => {
      state.selected =
        w.requirements.find((r) => r.categoryKey === category.key && r.state !== "not-applicable")
          ?.ruleId ?? null;
      go("requirements");
    });
    grid.append(cell);
  }
  frag.append(grid);

  return frag;
}

// -------------------------------------------------------------- requirements

function requirementDetail(r: RequirementView): HTMLElement {
  const detail = el("article", "detail");
  detail.append(el("p", "kicker", r.categoryName));

  const pill = el("span", `pill pill--${r.state === "not-applicable" ? "" : r.state}`.trim());
  pill.textContent = r.statusLabel;
  detail.append(pill);
  detail.append(el("h2", undefined, r.title));

  // The centrepiece of the design: the rule in ordinary English. Ours is
  // adviser-authored in the ruleset, so this is the real thing rather than the
  // placeholder the prototype falls back to for 19 of its 21 requirements.
  const plainly = el("section", "plainly");
  plainly.append(el("p", "label", "The rule, plainly"));
  plainly.append(el("p", undefined, r.plainly));
  const cite = el("p", "cite");
  const link = el("a", undefined, r.citation.label);
  link.href = r.citation.url;
  link.rel = "noopener";
  cite.append(link);
  cite.append(document.createTextNode(` · published ${formatDate(r.citation.published)}`));
  plainly.append(cite);
  detail.append(plainly);

  if (r.working.length > 0) {
    const working = el("div", "working");
    for (const step of r.working) {
      const row = el("div", "working-row");
      row.append(el("span", "expr", step.expression));
      row.append(el("span", "res", step.result));
      working.append(row);
      if (step.note) working.append(el("p", "working-note", step.note));
    }
    detail.append(working);
  }

  if (r.state === "judged") {
    detail.append(
      el(
        "p",
        "judged-note",
        "This one turns on a caseworker's judgement of your evidence rather than on a fact, so Verity never marks it met or not met. What it is judged on is above.",
      ),
    );
  }

  if (r.eligibleFrom) {
    detail.append(
      el(
        "p",
        "eligible-note",
        `On the answers you gave, this is first met on ${formatDate(r.eligibleFrom)}.`,
      ),
    );
  }

  if (r.evidence.length > 0) {
    const evidence = el("section", "evidence");
    evidence.append(el("h3", undefined, "What Verity needs to see"));
    const list = el("ul");
    for (const item of r.evidence) {
      const li = el("li", item.met ? "met" : "unmet");
      li.append(el("span", "mark", item.met ? "✓" : "○"));
      const body = el("div");
      body.append(el("div", "label", item.label));
      for (const note of item.notes) body.append(el("div", "note", note));
      li.append(body);
      list.append(li);
    }
    evidence.append(list);
    detail.append(evidence);
  }

  return detail;
}

function renderRequirements(w: Workspace): HTMLElement {
  const frag = el("div");

  const chips = el("div", "chips");
  for (const [key, label] of [
    ["all", "All"],
    ["open", "Still open"],
  ] as ["all" | "open", string][]) {
    const chip = el("button", "chip", label);
    chip.type = "button";
    chip.setAttribute("aria-pressed", String(state.filter === key));
    chip.addEventListener("click", () => {
      state.filter = key;
      render();
    });
    chips.append(chip);
  }
  frag.append(chips);

  const visible = w.requirements.filter((r) => {
    if (r.state === "not-applicable") return false;
    return state.filter === "all" ? true : r.state === "progress";
  });

  const selected =
    visible.find((r) => r.ruleId === state.selected) ?? visible[0] ?? null;
  if (selected) state.selected = selected.ruleId;

  const layout = el("div", "reqs");

  const list = el("ul", "req-list");
  if (visible.length === 0) {
    list.append(el("li", "req-empty", "Nothing is open — every requirement we can check is met."));
  }
  for (const r of visible) {
    const li = el("li");
    const button = el("button");
    button.type = "button";
    button.setAttribute("aria-current", String(r.ruleId === selected?.ruleId));
    button.append(el("span", `dot dot--${r.state}`));
    const body = el("span");
    body.append(el("span", "t", r.title));
    body.append(el("br"));
    body.append(el("span", "s", r.statusLabel));
    button.append(body);
    button.addEventListener("click", () => {
      state.selected = r.ruleId;
      render();
    });
    li.append(button);
    list.append(li);
  }
  layout.append(list);

  if (selected) layout.append(requirementDetail(selected));
  frag.append(layout);

  const limits = el("details", "limits");
  limits.append(el("summary", undefined, "What this does not look at"));
  const ul = el("ul");
  for (const limit of CHECK_LIMITS) ul.append(el("li", undefined, limit));
  limits.append(ul);
  frag.append(limits);

  return frag;
}

// ----------------------------------------------------------- financial check

function renderFinancial(w: Workspace): HTMLElement {
  const frag = el("div");
  const financial = w.requirements.filter(
    (r) => r.categoryKey === "financial" && r.state !== "not-applicable",
  );

  const carrier = financial.find((r) => r.working.length > 0);
  const card = el("section", "card");

  if (carrier) {
    const readiness = el("div", "readiness");
    const figure = el("div", "figure");
    figure.append(el("div", "label", carrier.title));
    figure.append(
      el("div", "pct", carrier.state === "settled" ? "Met" : "Not yet"),
    );
    readiness.append(figure);
    readiness.append(el("p", "interp", carrier.plainly));
    card.append(readiness);

    const working = el("div", "working");
    for (const step of carrier.working) {
      const row = el("div", "working-row");
      row.append(el("span", "expr", step.expression));
      row.append(el("span", "res", step.result));
      working.append(row);
      if (step.note) working.append(el("p", "working-note", step.note));
    }
    card.append(working);
  } else {
    card.append(
      el(
        "p",
        "interp",
        "There is no arithmetic to show for this case yet — the financial route has not been answered, or it is met without a calculation.",
      ),
    );
  }
  frag.append(card);

  const head = el("div", "section-head");
  head.append(el("h2", undefined, "Every financial requirement"));
  frag.append(head);

  for (const r of financial) frag.append(requirementDetail(r));
  return frag;
}

// ------------------------------------------------------------------- render

const TITLES: Record<View, { kicker: string; title: string }> = {
  overview: { kicker: "Your case", title: "Where you stand" },
  requirements: { kicker: "The application", title: "Requirements" },
  financial: { kicker: "The application", title: "Financial check" },
  documents: { kicker: "The application", title: "Documents" },
  risk: { kicker: "Getting it right", title: "Worth strengthening" },
  ask: { kicker: "Getting it right", title: "Ask Verity" },
  together: { kicker: "Getting it right", title: "Together" },
  submission: { kicker: "Submitting", title: "Submission day" },
  after: { kicker: "Submitting", title: "After you apply" },
  account: { kicker: "You", title: "Account & settings" },
};

function go(view: View): void {
  state.view = view;
  // Drop the fragment we arrived on. Without this a person who lands on
  // `#billing`, reads it and moves to Overview is put back on billing by the
  // next reload. `replaceState` rather than setting `location.hash`, so moving
  // between screens does not fill the back button with them.
  if (window.location.hash) {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  }
  render();
  byId("pane")?.focus();
}

function render(): void {
  const w = state.workspace;
  if (!w) return;

  for (const button of document.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]")) {
    const isCurrent = button.dataset["view"] === state.view;
    if (isCurrent) button.setAttribute("aria-current", "page");
    else button.removeAttribute("aria-current");
  }

  const heading = TITLES[state.view];
  const kicker = byId("kicker");
  const title = byId("title");
  if (kicker) kicker.textContent = heading.kicker;
  if (title) title.textContent = heading.title;

  const views: Record<View, () => HTMLElement> = {
    overview: () => renderOverview(w),
    requirements: () => renderRequirements(w),
    financial: () => renderFinancial(w),
    documents: renderDocuments,
    risk: renderRisk,
    ask: renderAsk,
    together: renderTogether,
    submission: renderSubmission,
    after: renderAfter,
    account: () =>
      renderAccount(state.email, state.initials, {
        people: state.people,
        tier: state.tier,
        payment: state.payment,
        paymentConfigured: state.paymentConfigured,
        onStartPayment: async () => {
          const url = await startCheckout();
          // Leaving the origin: Stripe's hosted page collects the card, so
          // nothing of theirs ever loads here (§3.8, prohibition 10).
          if (url) window.location.assign(url);
        },
        /**
         * Erasure. The server decides between erasing the household, removing
         * just this person, and retaining a client file (F14 D3), so the screen
         * reports what happened rather than assuming.
         *
         * SIGNED OUT AFTERWARDS, in every outcome. Their access has ended in
         * all three, and leaving them looking at a workspace that no longer
         * describes anything true is the worst of the options.
         */
        onDelete: async () => {
          let outcome: "erased" | "left" | "retained" | null = null;
          try {
            const response = await fetch("/api/me/delete", {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: "{}",
            });
            if (response.ok) {
              outcome = ((await response.json()) as { outcome?: typeof outcome }).outcome ?? null;
            }
          } catch {
            outcome = null;
          }
          // Long enough to read the line that says what happened. The session
          // is already void server-side; this is about them, not security.
          if (outcome) setTimeout(() => window.location.assign(LOGOUT_URL), 4000);
          return outcome;
        },
        onRevoke: async (kind, id) => {
          if (!(await post("/api/people/revoke", { kind, id }))) return;
          state.people = await loadPeople();
          render();
        },
      }),
  };

  for (const view of VIEWS) {
    const section = byId(`view-${view}`);
    if (!section) continue;
    section.hidden = view !== state.view;
    if (view === state.view) section.replaceChildren(views[view]());
  }

  // The rail is hidden on Account & settings — the handoff is explicit, and it
  // is right: that screen is about the person, not the case.
  const slot = byId("rail-slot");
  if (slot) {
    if (state.view === "account") slot.replaceChildren();
    else slot.replaceChildren(renderRail(state.workspace));
  }
}

function showShell(): void {
  const centered = byId("centered");
  const shell = byId("shell");
  if (centered) centered.hidden = true;
  if (shell) shell.hidden = false;
}

function showCentered(which: "signed-out" | "failed"): void {
  const centered = byId("centered");
  const shell = byId("shell");
  if (shell) shell.hidden = true;
  if (centered) centered.hidden = false;
  for (const id of ["signed-out", "failed"]) {
    const section = byId(id);
    if (section) section.hidden = id !== which;
  }
}

async function loadOnboarding(): Promise<OnboardingState | null> {
  try {
    const response = await fetch("/api/onboarding", { headers: { accept: "application/json" } });
    if (!response.ok) return null;
    return (await response.json()) as OnboardingState;
  } catch {
    return null;
  }
}

/**
 * The wizard, rendered instead of the workspace.
 *
 * The shell's navigation is disabled while it runs: half-finished sign-up is
 * not a state the dashboard, the requirements list or the rail know how to
 * describe, and letting somebody wander into them would show a case with no
 * role and no partner on it.
 */
function showOnboarding(state: OnboardingState, saved: CurrentCase | null): void {
  const section = byId("view-empty");
  if (!section) return;

  for (const view of VIEWS) {
    const other = byId(`view-${view}`);
    if (other) other.hidden = true;
  }
  for (const button of document.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]")) {
    button.disabled = true;
  }
  const slot = byId("rail-slot");
  if (slot) slot.replaceChildren();

  const panel = el("div", "panel-narrow onboarding");
  section.replaceChildren(panel);
  section.hidden = false;

  renderOnboarding(state, {
    save: (patch) => post("/api/onboarding", patch),
    // 404 is the honest answer when payment is not switched on, so the wizard
    // finishes anyway and Billing carries the outstanding state (F4).
    startPayment: startCheckout,
    invitePartner: (email) => post("/api/partner/invite", { email }),
    caseSummary: () => {
      if (!saved?.result) return null;
      const live = saved.result.requirements.filter((r) => r.status !== "NOT_APPLICABLE");
      return {
        verdict: saved.result.verdict,
        met: live.filter((r) => r.status === "MET").length,
        // Judged-by-caseworker requirements are not "open" — nothing the person
        // does closes them (ADR-0022) — so counting them here would tell
        // somebody they have work outstanding that does not exist.
        open: live.filter((r) => r.status === "NOT_MET" || r.status === "PENDING_EVIDENCE").length,
      };
    },
    // Reload rather than swapping the view in place: sign-up changes the
    // person's role and their partner's membership, and every screen after it
    // reads those. A fresh load is the honest way to pick them up.
    onFinished: () => window.location.reload(),
  }, panel);
}

async function post(path: string, body: unknown): Promise<boolean> {
  try {
    const response = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return response.ok;
  } catch {
    return false;
  }
}

interface CurrentCase {
  case_id: string;
  version: number;
  result: Determination;
  created_at: string;
  ruleset_version: string;
}

/** 404 means no case yet, which is a normal state and not an error (F6). */
async function loadCase(): Promise<CurrentCase | null> {
  try {
    const response = await fetch("/api/cases/current", {
      headers: { accept: "application/json" },
    });
    if (!response.ok) return null;
    return (await response.json()) as CurrentCase;
  } catch {
    return null;
  }
}

/**
 * A case the engine routed out.
 *
 * Before this, an out-of-scope determination produced a workspace with no
 * requirements in it — 0% ready, no categories, nothing to do — which reads as
 * a broken page rather than an answer. It is also the exact case that should
 * reach an adviser, since most routed-out reasons are ordinary applications
 * the engine does not encode yet rather than problems (ADR-0039).
 */
function renderRoutedOut(d: Determination): void {
  const section = byId("view-empty");
  if (!section || !d.out_of_scope) return;

  const pathway = pathwayFor(d.out_of_scope.flags);
  const panel = el("div", "panel-narrow");

  panel.append(el("h1", undefined, pathway?.heading ?? "This is outside what Verity covers"));
  for (const reason of d.out_of_scope.reasons) panel.append(el("p", "lede", reason));

  // Adviser-authored, case-specific, and kept whatever the pathway says.
  panel.append(el("p", undefined, d.out_of_scope.referral_hint));

  if (pathway) {
    panel.append(el("p", undefined, pathway.explanation));

    if (pathway.action.kind === "adviser_enquiry") {
      const cta = el("button", "btn btn-primary", pathway.action.label);
      cta.type = "button";
      cta.disabled = true;
      cta.title = "Available once Verity's registered advisers are live";
      const p = el("p");
      p.style.marginTop = "22px";
      p.append(cta);
      panel.append(p);
      panel.append(
        el(
          "p",
          "note",
          "Verity's own advisers are not taking cases yet, so this is not live. You do not have to wait for us.",
        ),
      );
    }

    const link = el("a", undefined, "Find a registered adviser on the IAA register");
    link.href = "https://www.gov.uk/find-an-immigration-adviser";
    link.rel = "noopener";
    const p2 = el("p", "note");
    p2.append(link);
    panel.append(p2);
  }

  section.replaceChildren(panel);
  section.hidden = false;

  const kicker = byId("kicker");
  const title = byId("title");
  if (kicker) kicker.textContent = "Your case";
  if (title) title.textContent = "Your case";
  for (const view of VIEWS) {
    const other = byId(`view-${view}`);
    if (other) other.hidden = true;
  }
  // The case screens have nothing to show; the rest stay browsable.
  for (const button of document.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]")) {
    const view = button.dataset["view"] as View;
    button.disabled = view === "overview" || view === "requirements" || view === "financial";
  }
  const slot = byId("rail-slot");
  if (slot) slot.replaceChildren(renderRail(null));
}

function renderEmpty(): void {
  const section = byId("view-empty");
  if (!section) return;
  const panel = el("div", "panel-narrow");
  panel.append(el("h1", undefined, "Nothing saved yet"));
  panel.append(
    el(
      "p",
      "lede",
      "Run the free check and email yourself the result. Do it in this browser and your answers will be waiting here — you will not have to type them twice.",
    ),
  );
  const p = el("p");
  p.style.marginTop = "22px";
  const link = el("a", "btn btn-primary", "Start the free check");
  link.href = "/check/";
  p.append(link);
  panel.append(p);
  section.replaceChildren(panel);
  section.hidden = false;
  const slot = byId("rail-slot");
  if (slot) slot.replaceChildren(renderRail(null));
  const kicker = byId("kicker");
  const title = byId("title");
  if (kicker) kicker.textContent = "Your case";
  if (title) title.textContent = "Your case";
  for (const view of VIEWS) {
    const other = byId(`view-${view}`);
    if (other) other.hidden = true;
  }
  // The case-driven screens have nothing to show; the rest are still worth
  // browsing, so only the three that need a determination are disabled.
  for (const button of document.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]")) {
    const view = button.dataset["view"] as View;
    button.disabled = view === "overview" || view === "requirements" || view === "financial";
  }
}

async function start(): Promise<void> {
  const principal = await whoami();
  if (!principal) {
    // Sign in and come BACK HERE — see `loginUrlForHere`. The static href in
    // index.html stays as it is and remains correct for anyone whose
    // JavaScript never ran; this only makes it carry the destination.
    const link = document.querySelector<HTMLAnchorElement>("#signed-out a[href^='/.auth/login']");
    if (link) link.href = loginUrlForHere(window.location);
    return showCentered("signed-out");
  }

  const name = byId("person-name");
  const avatar = byId("avatar");
  state.email = principal.userDetails;
  state.initials = initialsOf(principal.userDetails);
  if (name) name.textContent = state.email;
  if (avatar) avatar.textContent = state.initials;

  const session = await establishHousehold(principal, new Date());
  if (!session) return showCentered("failed");

  showShell();
  for (const button of document.querySelectorAll<HTMLButtonElement>(".nav-item[data-view]")) {
    button.addEventListener("click", () => go(button.dataset["view"] as View));
  }

  // SIGN-UP COMES BEFORE ANYTHING ELSE. Somebody who has not finished it has no
  // role on their own case and no partner on it — the dashboard would be asking
  // them to work a case that is not set up yet.
  // MINE, not the household's. The second person accepts an invitation to a
  // case that finished sign-up before their account existed — gating on the
  // household's stamp sent them straight to the dashboard having confirmed
  // nothing and seen no price.
  const onboarding = await loadOnboarding();
  if (onboarding && !onboarding.mine_completed_at) {
    // Step 3 shows what the check found, so the case is loaded alongside. No
    // case is a legitimate state here — it is what F6 exists for — and the
    // wizard says so rather than failing.
    return showOnboarding(onboarding, await loadCase());
  }

  // Who is on the case, and what was chosen. Fetched alongside rather than when
  // Account is first opened, so the screen does not appear empty and then fill
  // in. Both are small and neither blocks rendering — `loadPeople` returns an
  // empty list rather than throwing.
  //
  // The tier comes from the onboarding state, which got it from the server, so
  // `Plan and payment` shows the same figure the wizard did and the same one a
  // payment would use.
  const [people, payment, history] = await Promise.all([
    loadPeople(),
    loadPayment(),
    loadHistory(),
  ]);
  state.people = people;
  state.history = history;
  state.payment = payment.payment;
  state.paymentConfigured = payment.configured;

  // Coming straight back from Stripe, the row is very often still `pending`:
  // the card is saved, and `checkout.session.completed` has not reached the
  // webhook yet. Rendering that moment as it stands tells somebody who has just
  // finished "You started adding your card and did not finish", which is both
  // wrong and the most alarming thing the screen can say. So when the fragment
  // says we were sent here, give the webhook a moment before believing the row.
  //
  // ASK AGAIN, never assume. The alternative — trusting the return URL, which
  // is a thing in the address bar — would let anyone show themselves a saved
  // card by typing one. The server stays the only source of the status; all
  // this does is wait for it.
  if (viewFromHash(window.location.hash) === "account" && state.payment?.status === "pending") {
    state.payment = (await settlePayment()) ?? state.payment;
  }

  const asked = viewFromHash(window.location.hash);
  if (asked) state.view = asked;

  if (onboarding) {
    const chosen =
      onboarding.tiers.find((t) => t.id === onboarding.onboarding.tier_id) ?? null;
    state.tier = chosen
      ? { name: chosen.name, summary: chosen.summary, display: chosen.display }
      : null;
  }

  // The case is read from the household, not from a result id: whether a check
  // arrived by handoff, by save token, or was taken later inside the app (F6),
  // the dashboard asks the same question — what is this household working on?
  const saved = await loadCase();
  // No case is a NORMAL state, not a failure: somebody who signed up without
  // checking first is exactly who F6 exists for. The old code showed "we could
  // not load your case" here, which told them something had gone wrong when
  // nothing had.
  if (!saved?.result) return renderEmpty();
  if (saved.result.verdict === "OUT_OF_SCOPE") return renderRoutedOut(saved.result);

  const workspace = buildWorkspace(saved.result, ruleset);
  // Attach the arithmetic, which only the engine's presentation layer can
  // produce, so the page and the emailed copy cannot show different sums.
  for (const requirement of workspace.requirements) {
    const source = saved.result.requirements.find((r) => r.rule_id === requirement.ruleId);
    if (source) requirement.working = financialWorking(source);
  }
  state.workspace = workspace;

  const route = byId("route-label");
  if (route) route.textContent = `Partner visa · ${saved.result.as_of ? "UK" : "UK"}`;

  const navReqs = byId("nav-reqs");
  if (navReqs) {
    navReqs.textContent = `${workspace.settled} / ${workspace.settled + workspace.outstanding}`;
  }
  const navFin = byId("nav-fin");
  const financial = workspace.categories.find((c) => c.key === "financial");
  if (navFin && financial) navFin.textContent = `${financial.settled} / ${financial.total}`;

  // The handoff's target-date card, filled only when the engine has computed a
  // date. It is never a guess: NOT_READY carries `eligible_from` or the card
  // does not appear.
  const eligible = saved.result.not_ready?.eligible_from;
  const target = byId("target-card");
  if (eligible && target) {
    const date = byId("target-date");
    const note = byId("target-note");
    if (date) date.textContent = formatDate(eligible);
    if (note) {
      note.textContent = "The earliest date every time-based condition is satisfied.";
    }
    target.hidden = false;
  }

  render();
}

void start();
